import type { DateTime } from 'luxon'
import type { z } from 'zod'

import type {
  getAbortPrequalificationSchema,
  getAssignResourcesToActivationGroupScheduleSchema,
  getAssignResourceToActivationGroupSchema,
  getCreateActivationGroupSchema,
  getCreatePrequalificationPatternSchema,
  getGenericCreatePrequalificationPatternSchema,
  getGenericCreateScheduleSchema,
  getGenericStartPrequalificationSchema,
  getStartPrequalificationSchema,
  getUpdateActivationGroupSchema,
} from '@/features/activationGroup/schemas'
import type { MarketProgram } from '@/features/bidding/constants'
import type { Portfolio } from '@/features/bidding/types/bid'
import type { ResourceLifecycleStage } from '@/features/resource/types'

export enum ApiVolumeRangeUnit {
  WATTS = 'WATTS',
  WATT_HOURS = 'WATT_HOURS',
}

export type ApiBiddableVolumeRange = {
  unit: ApiVolumeRangeUnit
  min: number
  max: number
}

export enum PrequalificationLane {
  PQ1 = 'PQ1',
}

export enum ActivationScheduleType {
  BID = 'BID',
  PQ = 'PQ',
  DEMO = 'DEMO',
}

export enum ActivationScheduleStatus {
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
}

export enum ResourceCategory {
  MIXED = 'MIXED',
  BESS = 'BESS',
  C_AND_I = 'C_AND_I',
}

export enum ActivationProvider {
  SYMPOWER_LOCAL = 'SYMPOWER_LOCAL',
  SYMPOWER_CENTRAL = 'SYMPOWER_CENTRAL',
  FLEX_TOOLS = 'FLEX_TOOLS',
}

export type ApiActivationGroup = {
  uuid: string
  code: string
  portfolio: Portfolio
  marketProgram: MarketProgram
  resourceCategory: ResourceCategory
  biddableVolumeRange: ApiBiddableVolumeRange | null
  regulationObjectCode: string | null
  transmissionArea: string | null
  tradingStrategy: string | null
  reportingCode: string | null
  externalCode: string | null
  activationProvider: ActivationProvider | null
}

export type ApiCreateActivationGroup = {
  uuid: string
  code: string
  portfolioCode: string
  marketProgram: MarketProgram
  resourceCategory: ResourceCategory
  biddableVolumeRange: ApiBiddableVolumeRange | null
  resources: ApiPartialActivationGroupResource[]
  regulationObjectCode: string | null
  transmissionArea: string | null
  tradingStrategy: string | null
  reportingCode: string | null
  externalCode: string | null
  activationProvider: ActivationProvider | null
}

export type ApiUpdateActivationGroup = {
  code: string
  resourceCategory: ResourceCategory
  biddableVolumeRange: ApiBiddableVolumeRange | null
  resources: ApiPartialActivationGroupResource[]
  state: string
  regulationObjectCode: string | null
  transmissionArea: string | null
  tradingStrategy: string | null
  reportingCode: string | null
  externalCode: string | null
  activationProvider: ActivationProvider | null
}

export type ApiActivationGroupResource = {
  uuid: string
  activationPriority: number
  name: string
  // Verified capaciy in watts
  verifiedCapacity: number
  qualifiedCapacity: number | null
  lifecycleStage: ResourceLifecycleStage
  siteUuid: string | null
  isActivatable: boolean
}

export type ApiPartialActivationGroupResource = {
  uuid: string
  qualifiedCapacity: number | null
  activationPriority: number | null
  isActivatable: boolean
}

export type ApiPrequalification = {
  uuid: string
  startedAt: string
  endedAt: string | null
  prequalificationLane: PrequalificationLane
}

export type GenericApiPrequalification = {
  uuid: string
  startedAt: string
  simulationStartedAt: string | null
  simulationFinishedAt: string | null
  abortedAt: string | null
  endedAt: string | null
  portfolioCode: string
  activationTriggerChannel: number
}

export type ApiPrequalificationPage = {
  results: ApiPrequalification[]
  currentPage: number
  totalResults: number
  totalPages: number
}

export type GenericApiPrequalificationPage = {
  results: GenericApiPrequalification[]
  currentPage: number
  totalResults: number
  totalPages: number
}

/**
 * Dates are in ISO 8601 format
 */
export type ApiCreateActivationGroupSchedule = {
  activationGroupUuid: string
  startAt: string
  endAt: string
  // Sold capacity in watts
  soldCapacity: number
}

/**
 * Dates are in ISO 8601 format
 */
export type GenericApiCreateActivationGroupSchedule = {
  activationGroupUuid: string
  activationTriggerChannel: number
  scheduleType: ActivationScheduleType
  startAt: string
  endAt: string
  volume: number
  // By default, add all the resources from the Activation Group to the schedule
  includeAllResources: boolean
}

export type GenericApiUpdateActivationGroupSchedule = {
  volume: number
  endAt: string
  resources: string[]
}

export type GenericApiActivationGroupScheduleResourcePage = {
  results: GenericApiActivationGroupScheduleResource[]
  currentPage: number
  totalResults: number
  totalPages: number
}

export type GenericApiActivationGroupScheduleResource = {
  uuid: string
  name: string
  // null = UNKNOWN: prequalification-manager does not know the flag (past schedule, or the resource
  // is no longer a member of the activation group).
  isActivatable: boolean | null
}

export type ApiStartPrequalification = {
  prequalificationPatternUuid: string
  prequalificationLane: PrequalificationLane
}

export type ApiAbortPrequalification = {
  prequalificationLane: PrequalificationLane
}

export type GenericApiStartPrequalification = {
  prequalificationPatternUuid: string
  activationTriggerChannel: number
}

export type GenericApiAbortPrequalification = object

export type ActivationGroupUuid = string

export enum ActivationGroupState {
  LIVE = 'LIVE',
  NOT_LIVE = 'NOT_LIVE',
}

export type ActivationGroup = {
  uuid: ActivationGroupUuid
  code: string
  portfolio: Portfolio
  marketProgram: MarketProgram
  resourceCategory: ResourceCategory
  biddableVolumeRange: ApiBiddableVolumeRange | null
  state?: ActivationGroupState
  regulationObjectCode?: string | null
  transmissionArea?: string | null
  tradingStrategy?: string | null
  externalCode?: string | null
  reportingCode?: string | null
  activationProvider?: ActivationProvider | null
}

export type ActivationGroupSchedule = {
  uuid: string
  startAt: string
  endAt: string
  soldCapacity: number
  origin: 'LOCAL' | 'EXTERNAL'
}

export type GenericActivationGroupSchedule = {
  uuid: string
  portfolioCode: string
  activationTriggerChannel: number
  // scheduleType: 'BID' | 'PQ' | 'DEMO'
  scheduleType: ActivationScheduleType
  startAt: string
  endAt: string
  volume: number
  status: ActivationScheduleStatus
}

export type ActivationGroupSchedulesPage = {
  activationGroupSchedules: ActivationGroupSchedule[]
  currentPage: number
  totalActivationGroupSchedules: number
}

export type GenericActivationGroupSchedulesPage = {
  activationGroupSchedules: GenericActivationGroupSchedule[]
  currentPage: number
  totalActivationGroupSchedules: number
}

export type GenericActivationTriggerChannels = {
  activationTriggerChannels: number[]
}

export type GenericActivationGroupPrequalificationPattern = {
  uuid: string
  name: string
  pattern: string
  prequalifications: GenericPrequalification[]
}

export type Prequalification = {
  uuid: string
  startedAt: DateTime
  endedAt: DateTime | null
  prequalificationLane: PrequalificationLane
}

export type GenericPrequalification = {
  uuid: string
  startedAt: DateTime
  simulationStartedAt: DateTime | null
  simulationFinishedAt: DateTime | null
  abortedAt: DateTime | null
  endedAt: DateTime | null
  portfolioCode: string
  activationTriggerChannel: number
}

export type GenericActivationGroupPrequalificationPatternsPage = {
  activationGroupPrequalificationPatterns: GenericActivationGroupPrequalificationPattern[]
  currentPage: number
  totalActivationGroupPrequalificationPatterns: number
}

export type PrequalificationsPage = {
  prequalifications: Prequalification[]
  currentPage: number
  totalCount: number
}

export type GenericPrequalificationsPage = {
  prequalifications: GenericPrequalification[]
  currentPage: number
  totalCount: number
}

export type CreatePrequalificationPattern = z.infer<ReturnType<typeof getCreatePrequalificationPatternSchema>>

export type GenericCreatePrequalificationPattern = z.infer<
  ReturnType<typeof getGenericCreatePrequalificationPatternSchema>
>

export type CreateActivationGroup = z.infer<ReturnType<typeof getCreateActivationGroupSchema>>

export type UpdateActivationGroup = z.infer<ReturnType<typeof getUpdateActivationGroupSchema>>

export type AssignResourceToActivationGroup = z.infer<ReturnType<typeof getAssignResourceToActivationGroupSchema>>

export type GenericCreateSchedule = z.infer<ReturnType<typeof getGenericCreateScheduleSchema>>

export type StartPrequalification = z.infer<ReturnType<typeof getStartPrequalificationSchema>>

export type AbortPrequalification = z.infer<ReturnType<typeof getAbortPrequalificationSchema>>

export type GenericStartPrequalification = z.infer<ReturnType<typeof getGenericStartPrequalificationSchema>>

export type AssignResourceToActivationGroupSchedule = z.infer<
  ReturnType<typeof getAssignResourcesToActivationGroupScheduleSchema>
>
